import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Box,
  Button,
  Divider,
} from "@mui/material";
import React, { useState, useEffect, useMemo, useRef } from "react";
import html2canvas from "html2canvas";
import { useQuestionRecord } from "../../../context/QuestionRecord";
import { useProfile } from "../../../context/ProfileContext";
import ROUTES_NAME from "../../../constants/RoutesName";
import { useNavigate } from "react-router-dom";
import Loader from "../../../components/Loader";
import {
  submitStatus,
  submitApi,
  subjectSubmitApi,
  submitWarningSubWise,
  timeTakenApi,
  fetchSummaryApi,
  addSummaryPageLogApi,
  uploadExamSummaryScreenshotApi,
} from "../../../services/service";
import socket from "../../../socket/socket";
import { useLocation } from "react-router-dom";

function SubmitSummary({
  sectionNames,
  question,
  setIsOnSubmit,
  sscTimeUp,
  handleRemove,

  subjectNames,
  activeSubjectId,
  nextSubject,
  setActiveSubjectId,
  setActiveSectionId,
  saveBeforeSubmitRef,
  handleSubjectSubmit,
  currentQuestionId,
}) {
  const { questionRecords } = useQuestionRecord();
  const { profile } = useProfile();
  const [submitSummaryD, setSubmitSummaryD] = useState([]);
  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [countdownTime, setCountdownTime] = useState(10);
  const navigate = useNavigate();

  const rollNumber =
    profile?.learner_information?.roll_number ||
    profile?.learner_info?.roll_number ||
    profile?.roll_number ||
    profile?.roll_no ||
    sessionStorage.getItem("rollNo") ||
    (() => {
      try {
        return JSON.parse(sessionStorage.getItem("userAuth"))?.username;
      } catch (e) {
        return null;
      }
    })();

  const [stats, setStats] = useState({});
  const examId = sessionStorage.getItem("examId");
  const location = useLocation();
  const summaryPageRef = useRef(null);
  const screenshotBase64Ref = useRef(null);

  const saveSummaryScreenshot = async () => {
    const target = summaryPageRef.current;
    const examId = sessionStorage.getItem("examId") || "exam-summary";

    try {
      let screenshotBase64 = screenshotBase64Ref.current;

      if (!screenshotBase64 && target) {
        const canvas = await html2canvas(target, {
          backgroundColor: "#ffffff",
          scale: 2,
          useCORS: true,
          logging: false,
        });
        screenshotBase64 = canvas.toDataURL("image/png");
      }

      if (examId && screenshotBase64) {
        await uploadExamSummaryScreenshotApi(examId, screenshotBase64);
      }
    } catch (error) {
      console.error("Failed to save exam summary screenshot:", error);
    }
  };

  // Memoized initial summary creation
  const initialSummary = useMemo(() => {
    if (question?.subjects) {
      return question.subjects.flatMap((subject) => ({
        subject_id: subject.id || 0,
        subject_name: subject.subject_name || "Unnamed Subject",
        sections: (subject.sections || []).map((section) => ({
          section_id: section.id || 0,
          section_name: section.section_name || "Unnamed Section",
          totalQuestion: Number(section.question_count) || 0,
          answered: 0,
          notAns: 0,
          markedWithAns: 0,
          markedWithoutAns: 0,
          notVisited: Number(section.question_count) || 0,
        })),
      }));
    } else if (sectionNames?.length) {
      return [
        {
          subject_id: 0,
          subject_name: "Combined Sections",
          sections: sectionNames.flat().map((section) => ({
            section_id: section.section_id,
            section_name: section.name || "Unnamed Section",
            totalQuestion: 0,
            answered: 0,
            notAns: 0,
            markedWithAns: 0,
            markedWithoutAns: 0,
            notVisited: 0,
          })),
        },
      ];
    }
    return [];
  }, [question, sectionNames]);

  // Initialize or update summary data
  useEffect(() => {
    const examId = Number(sessionStorage.getItem("examId"));

    if (examId) {
      addSummaryPageLogApi({
        exam_id: examId,
        message: "Student viewed summary page",
      }).catch(() => { });
    }

    fetchSummary();
  }, []);

  const fetchSummary = async () => {
    const examId = sessionStorage.getItem("examId");

    if (!examId) {
      setSubmitSummaryD(initialSummary || []);
      return;
    }

    setSummaryLoading(true);

    try {
      const response = await fetchSummaryApi(examId);
      const summaryData = Array.isArray(response?.subjects)
        ? response.subjects
        : initialSummary || [];

      setSubmitSummaryD(summaryData);
    } catch (error) {
      console.error("Failed to fetch IPMAT exam summary:", error);
      setSubmitSummaryD(initialSummary || []);
    } finally {
      setSummaryLoading(false);
    }
  };
  // useEffect(() => {
  //     const saveProgress = async () => {
  //       try {
  //         if (saveBeforeSubmitRef?.current) {
  //           await saveBeforeSubmitRef.current();
  //         }
  //       } catch (e) {
  //         console.warn("Could not save question on summary mount:", e);
  //       }
  //     };
  //     saveProgress();
  //   }, []);
  const submitWarningAPI = async () => {
    // console.log(examId, activeSubjectId);
    try {
      const response = await submitWarningSubWise(examId, activeSubjectId);

      if (response.success) {
        console.log("stats?.submitWarningData?.sectionWiseStats?", response);
        setStats({
          submitWarningData: response, // Adjust based on your API response
        });
      }
    } catch (error) {
      console.error("Error in submitWarningAPI:", error);
    }
  };

  useEffect(() => {
    submitWarningAPI();
  }, []);

  // Memoized section question counts
  const updatedSummary = useMemo(() => {
    if (!submitSummaryD.length || !questionRecords?.length)
      return submitSummaryD;

    // Create a map for quick section_id lookup
    const sectionMap = new Map(
      questionRecords.map((q) => [Number(q.section_id), q]),
    );

    return submitSummaryD.map((subject) => ({
      ...subject,
      sections: subject.sections.map((section) => {
        const sectionQuestions = questionRecords.filter(
          (q) => Number(q.section_id) === Number(section.section_id),
        );

        const answered = sectionQuestions.reduce(
          (count, q) => (q.attempt_status === "attempted" ? count + 1 : count),
          0,
        );

        const notAns = sectionQuestions.reduce(
          (count, q) =>
            q.attempt_status === "not answered" ? count + 1 : count,
          0,
        );

        const markedWithAns = sectionQuestions.reduce(
          (count, q) =>
            q.attempt_status === "marked" &&
              (q.option_id || q.multiple_option_ids?.length || q.answer)
              ? count + 1
              : count,
          0,
        );

        const markedWithoutAns = sectionQuestions.reduce(
          (count, q) =>
            q.attempt_status === "marked" &&
              !q.option_id &&
              !q.multiple_option_ids?.length &&
              !q.answer
              ? count + 1
              : count,
          0,
        );

        const notVisited = Math.max(
          0,
          section.totalQuestion -
          (answered + markedWithAns + markedWithoutAns + notAns),
        );

        return {
          ...section,
          answered,
          notAns,
          markedWithAns,
          markedWithoutAns,
          notVisited,
        };
      }),
    }));
  }, [questionRecords, submitSummaryD]);

  // Update summary only when counts actually change
  useEffect(() => {
    const shouldUpdate =
      JSON.stringify(submitSummaryD) !== JSON.stringify(updatedSummary);
    if (shouldUpdate) {
      setSubmitSummaryD(updatedSummary);
    }
  }, [updatedSummary]);

  // const handleRemove = () => {
  //   setIsOnSubmit(false);
  //   sessionStorage.removeItem("isOnsubmit");
  //   window.location.reload();
  // };

  useEffect(() => {
    if (sscTimeUp) {
      const timer = setInterval(() => {
        setCountdownTime((prevTime) => {
          const newTime = prevTime - 1;
          return newTime;
        });
      }, 1000);

      const timeout = setTimeout(() => {
        clearInterval(timer);
        if (sessionStorage.getItem("subject_submit") !== "yes") {
          const centerFeedback = sessionStorage.getItem("center_feedback");
          if (sessionStorage.getItem("submitByH")) {
            setLoading(false);
            const examId = sessionStorage.getItem("examId");
            const userId = sessionStorage.getItem("userId");
            const branch_id = sessionStorage.getItem("selectedBranch");
            const center_id = sessionStorage.getItem("selectedCenter");
            const userAuth = JSON.parse(sessionStorage.getItem("userAuth"));
            const exam_name = sessionStorage.getItem("exam_name");
            const examToken = sessionStorage.getItem("examToken");
            const userName = sessionStorage.getItem("userName");
            const demoExamId = sessionStorage.getItem("demoExamId");
            sessionStorage.clear();
            sessionStorage.setItem("exam_name", exam_name);
            sessionStorage.setItem("userId", userId);
            sessionStorage.setItem("selectedBranch", branch_id);
            sessionStorage.setItem("selectedCenter", center_id);
            sessionStorage.setItem("userAuth", JSON.stringify(userAuth));
            sessionStorage.setItem("candidateLoggedinToDashboard", true);
            if (centerFeedback) {
              sessionStorage.setItem("center_feedback", centerFeedback);
            }
            if (centerFeedback === "yes") {
              sessionStorage.setItem("examId", examId);
              sessionStorage.setItem("examToken", examToken);
              sessionStorage.setItem("userName", userName);
              if (demoExamId) {
                sessionStorage.setItem("demoExamId", demoExamId);
              }
            }

            if (centerFeedback === "yes") {
              navigate(ROUTES_NAME.CENTER_FEEDBACK);
            } else {
              navigate(ROUTES_NAME.THANK_YOU);
            }
          } else {
            submit("time");
            if (centerFeedback === "yes") {
              navigate(ROUTES_NAME.CENTER_FEEDBACK);
            } else {
              navigate(ROUTES_NAME.THANK_YOU);
            }
          }
        } else {
          submit("time");
          navigate(ROUTES_NAME.TEST);
        }
      }, 10000);

      return () => {
        clearTimeout(timeout);
        clearInterval(timer);
      };
    }
  }, [sscTimeUp]);

  const submit = async (type) => {
    const examId = sessionStorage.getItem("examId");
    await saveSummaryScreenshot();
    // Fire time_taken API silently on every submit (user/time/head)
    try {
      timeTakenApi({
        time_taken: {
          exam_id: examId || null,
          question_id: currentQuestionId || null,
          time_taken: Number(sessionStorage.getItem("temp_time")) || 0,
        },
      }).catch(() => { });
    } catch (_) { }
    setLoading(true);
    const subjectCurrentIndex = subjectNames?.findIndex(
      (item) => item.subject_id === activeSubjectId,
    );

    const isLastSectionOfLastSubject = sessionStorage.getItem("subject_selection") === "yes" && sessionStorage.getItem('userId') !== '4660' ? sessionStorage.getItem("lastSubject") :
      subjectCurrentIndex === subjectNames?.length - 1;

    if (sessionStorage.getItem("submitByH")) {
      setLoading(false);
      const examId = sessionStorage.getItem("examId");
      const userId = sessionStorage.getItem("userId");
      const branch_id = sessionStorage.getItem("selectedBranch");
      const center_id = sessionStorage.getItem("selectedCenter");
      const userAuth = JSON.parse(sessionStorage.getItem("userAuth"));
      const exam_name = sessionStorage.getItem("exam_name");
      const centerFeedback = sessionStorage.getItem("center_feedback");
      const examToken = sessionStorage.getItem("examToken");
      const userName = sessionStorage.getItem("userName");
      const demoExamId = sessionStorage.getItem("demoExamId");
      sessionStorage.clear();
      sessionStorage.setItem("exam_name", exam_name);
      sessionStorage.setItem("userId", userId);
      sessionStorage.setItem("selectedBranch", branch_id);
      sessionStorage.setItem("selectedCenter", center_id);
      sessionStorage.setItem("userAuth", JSON.stringify(userAuth));
      sessionStorage.setItem("candidateLoggedinToDashboard", true);
      if (centerFeedback) {
        sessionStorage.setItem("center_feedback", centerFeedback);
      }
      if (centerFeedback === "yes") {
        sessionStorage.setItem("examId", examId);
        sessionStorage.setItem("examToken", examToken);
        sessionStorage.setItem("userName", userName);
        if (demoExamId) {
          sessionStorage.setItem("demoExamId", demoExamId);
        }
      }

      if (centerFeedback === "yes") {
        navigate(ROUTES_NAME.CENTER_FEEDBACK);
      } else {
        navigate(ROUTES_NAME.THANK_YOU);
      }
      return;
    }

    if (isLastSectionOfLastSubject || sessionStorage.getItem("userId") === "4660") {
      if (handleSubjectSubmit) {
        await handleSubjectSubmit(true);
      }
      try {
        const res = await submitApi({ examId, type });
        if (res.success) {
          setLoading(false);
          const examId = sessionStorage.getItem("examId");
          const userId = sessionStorage.getItem("userId");
          socket.emit("changeStatus", {
            examId,
            userId,
            status: "Submitted",
          });
          const branch_id = sessionStorage.getItem("selectedBranch");
          const center_id = sessionStorage.getItem("selectedCenter");
          const userAuth = JSON.parse(sessionStorage.getItem("userAuth"));
          const exam_name = sessionStorage.getItem("exam_name");
          const centerFeedback = sessionStorage.getItem("center_feedback");
          const examToken = sessionStorage.getItem("examToken");
          const userName = sessionStorage.getItem("userName");
          const demoExamId = sessionStorage.getItem("demoExamId");
          sessionStorage.clear();
          sessionStorage.setItem("exam_name", exam_name);
          sessionStorage.setItem("userId", userId);
          sessionStorage.setItem("selectedBranch", branch_id);
          sessionStorage.setItem("selectedCenter", center_id);
          sessionStorage.setItem("userAuth", JSON.stringify(userAuth));
          sessionStorage.setItem("candidateLoggedinToDashboard", true);
          if (centerFeedback) {
            sessionStorage.setItem("center_feedback", centerFeedback);
          }
          if (centerFeedback === "yes") {
            sessionStorage.setItem("examId", examId);
            sessionStorage.setItem("examToken", examToken);
            sessionStorage.setItem("userName", userName);
            if (demoExamId) {
              sessionStorage.setItem("demoExamId", demoExamId);
            }
          }

          if (userId === "4660") {
            navigate(ROUTES_NAME.EXAM_SCORE, {
              state: { examResult: JSON.parse(res.examResult).result },
            });
            sessionStorage.setItem("examId", examId);
          } else {
            if (centerFeedback === "yes") {
              navigate(ROUTES_NAME.CENTER_FEEDBACK);
            } else {
              navigate(ROUTES_NAME.THANK_YOU);
            }
          }
        }
      } catch (error) {
        console.log("error", error);
        setLoading(false);
      }
    } else {
      try {
        const res = await subjectSubmitApi(examId, activeSubjectId);
        if (res.success) {
          setLoading(false);
          nextSubject();
          const submitted_subjects = JSON.parse(
            sessionStorage.getItem("submitted_subjects") || "[]",
          );
          sessionStorage.setItem(
            "submitted_subjects",
            JSON.stringify([...submitted_subjects, activeSubjectId]),
          );
          const subjectCurrentIndex = subjectNames?.findIndex(
            (item) => item.subject_id === activeSubjectId,
          );
          setActiveSubjectId(subjectNames[subjectCurrentIndex + 1].subject_id);
          setActiveSectionId(
            sectionNames[subjectCurrentIndex + 1][0].section_id,
          );
          handleRemove();
          if (sessionStorage.getItem("subject_selection") === "yes" && sessionStorage.getItem('userId') !== '4660') {
            sessionStorage.removeItem("examStarted");
            navigate(ROUTES_NAME.SUBJECT_SELECTION);
            sessionStorage.setItem("subjectSelection", true);
          }
          // navigate(ROUTES_NAME.TEST);
        }
      } catch (error) {
        console.log("error", error);
        setLoading(false);
      }
    }

    // nextSubject
  };

  useEffect(() => {
    socket.connect();
    return () => {
      socket.disconnect();
    };
  }, []);

  return (
    <>
      {summaryLoading && <Loader message={"Loading exam summary..."} />}

      {loading && (
        <Loader
          message={
            "Your exam will be submitted in a few seconds, please wait..."
          }
        />
      )}
      <div ref={summaryPageRef}>
        {agree ? (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "80vh",
                width: "100%",
                px: 2,
              }}
            >
              <Box sx={{ width: "100%", maxWidth: "400px" }}>
                <Typography
                  variant="body1"
                  sx={{
                    textAlign: "center",
                    fontWeight: "600",
                    fontSize: { xs: "1.1rem", sm: "1.25rem" },
                    mb: 3,
                  }}
                >
                  Are you sure to submit exam?
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 2,
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      submit("user");
                    }}
                    sx={{ minWidth: 120, borderRadius: "8px" }}
                  >
                    Yes
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleRemove}
                    sx={{ minWidth: 120, borderRadius: "8px" }}
                  >
                    No
                  </Button>
                </Box>
              </Box>
            </Box>
          </>
        ) : (
          <>
            <Typography
              variant="h6"
              sx={{
                mt: 2,
                fontWeight: "bold",
                textAlign: "center",
                borderBottom: "1px solid black",
                paddingBottom: "10px",
              }}
            >
              Exam Summary
            </Typography>
            {sscTimeUp && countdownTime && (
              <Typography
                variant="body1"
                sx={{
                  mt: 2,
                  fontWeight: "600",
                  textAlign: "center",
                  borderBottom: "1px solid black",
                  paddingBottom: "10px",
                }}
              >
                {sessionStorage.getItem("submitByH")
                  ? "Your exam has been submitted by the head authority!"
                  : "Time Up"}{" "}
                : {countdownTime}s
              </Typography>
            )}

            <Box
              sx={{
                overflowY: "auto",
                height: {
                  xs:
                    countdownTime === 10
                      ? "calc(100vh - 280px)"
                      : "calc(100vh - 140px)",
                  sm: "calc(100vh - 220px)",
                  md: "calc(100vh - 200px)",
                },
                pb: 2,
              }}
            >
              <Box px={{ xs: 1, sm: 2 }} pt={{ xs: 1, sm: 1.5 }}>
                <Typography
                  sx={{
                    py: 0.5,
                    fontWeight: "600",
                    fontSize: { xs: "0.9rem", sm: "1rem" },
                    color: "#333",
                  }}
                >
                  Roll No : {rollNumber || "N/A"}
                </Typography>
              </Box>

              {submitSummaryD.map((subject, sIdx) => (
                <Box p={{ xs: 1, sm: 2 }} key={sIdx}>
                  <Typography
                    sx={{
                      py: 1,
                      fontWeight: "600",
                      fontSize: { xs: "0.9rem", sm: "1rem" },
                      color: "#333",
                    }}
                  >
                    Subject : {subject.subject_name}
                  </Typography>
                  <TableContainer
                    component={Paper}
                    sx={{
                      boxShadow: 1,
                      borderRadius: "8px",
                      overflowX: "auto",
                    }}
                  >
                    <Table
                      size="small"
                      aria-label="exam summary table"
                      sx={{ minWidth: { xs: 750, sm: "auto" } }}
                    >
                      <TableHead sx={{ backgroundColor: "#f8f9fa" }}>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: "600",
                              fontSize: { xs: "0.75rem", sm: "0.85rem" },
                              py: 1.5,
                            }}
                          >
                            Section Name
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              fontWeight: "600",
                              fontSize: { xs: "0.75rem", sm: "0.85rem" },
                            }}
                          >
                            Total
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              fontWeight: "600",
                              fontSize: { xs: "0.75rem", sm: "0.85rem" },
                            }}
                          >
                            Answered
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              fontWeight: "600",
                              fontSize: { xs: "0.75rem", sm: "0.85rem" },
                            }}
                          >
                            Not Ans
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              fontWeight: "600",
                              fontSize: { xs: "0.75rem", sm: "0.85rem" },
                            }}
                          >
                            Marked
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              fontWeight: "600",
                              fontSize: { xs: "0.75rem", sm: "0.85rem" },
                            }}
                          >
                            Ans & Marked
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              fontWeight: "600",
                              fontSize: { xs: "0.75rem", sm: "0.85rem" },
                            }}
                          >
                            Not Visited
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {subject.sections?.map((section, index) => (
                          <TableRow key={index} hover>
                            <TableCell
                              sx={{
                                fontSize: { xs: "0.75rem", sm: "0.85rem" },
                              }}
                            >
                              {section.section_name}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                fontSize: { xs: "0.75rem", sm: "0.85rem" },
                              }}
                            >
                              {section.totalQuestion}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                fontSize: { xs: "0.75rem", sm: "0.85rem" },
                              }}
                            >
                              {section.answered}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                fontSize: { xs: "0.75rem", sm: "0.85rem" },
                              }}
                            >
                              {section.notAns}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                fontSize: { xs: "0.75rem", sm: "0.85rem" },
                              }}
                            >
                              {section.markedWithoutAns}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                fontSize: { xs: "0.75rem", sm: "0.85rem" },
                              }}
                            >
                              {section.markedWithAns}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                fontSize: { xs: "0.75rem", sm: "0.85rem" },
                              }}
                            >
                              {section.notVisited}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ))}
            </Box>

            {countdownTime === 10 && !sscTimeUp ? (
              <Box
                sx={{
                  position: "fixed",
                  width: "100%",
                  bottom: 0,
                  borderTop: "1px solid #ccc",
                  py: 1.5,
                  bgcolor: "#fff",
                  boxShadow: "0 -2px 10px rgba(0,0,0,0.05)",
                  zIndex: 10,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    margin: "auto",
                    width: { xs: "90%", sm: "80%", md: "60%" },
                    textAlign: "center",
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    lineHeight: 1.4,
                    color: "#555",
                  }}
                >
                  Are you sure to submit this Subject? Click 'Yes' to proceed;
                  Click 'No' to go back. Once submitted, you cannot edit your
                  responses.
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    mt: 1.5,
                    gap: 2,
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      if (summaryPageRef.current) {
                        html2canvas(summaryPageRef.current, {
                          backgroundColor: "#ffffff",
                          scale: 2,
                          useCORS: true,
                          logging: false,
                        })
                          .then((canvas) => {
                            screenshotBase64Ref.current = canvas.toDataURL("image/png");
                          })
                          .catch(() => { });
                      }
                      setAgree(true);
                    }}
                    sx={{
                      minWidth: 100,
                      textTransform: "none",
                      borderRadius: "6px",
                    }}
                  >
                    Yes
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleRemove}
                    sx={{
                      minWidth: 100,
                      textTransform: "none",
                      borderRadius: "6px",
                    }}
                  >
                    No
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  position: "fixed",
                  width: "100%",
                  bottom: 0,
                  borderTop: "1px solid #ccc",
                  py: 2,
                  display: "flex",
                  justifyContent: "center",
                  bgcolor: "#fff",
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    submit("time");
                  }}
                  sx={{
                    minWidth: 150,
                    textTransform: "none",
                    borderRadius: "8px",
                    fontWeight: "bold",
                  }}
                >
                  Submit Exam
                </Button>
              </Box>
            )}
          </>
        )}
      </div>
    </>
  );
}

export default SubmitSummary;
